import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useFirebaseAuth } from "@/hooks/useFirebaseAuth";
import { i18n } from "@/i18n";
import { eden } from "@/lib/eden";
import { edenData } from "@/lib/eden-data";
import { queryKeys } from "@/lib/query-keys";
import type { Subscription } from "@/types/billing";

/**
 * Single cached source of truth for the active merchant's subscription.
 * Every consumer shares this query and `refetch` updates all of them.
 */
export function useSubscription() {
  const { isAuthenticated } = useFirebaseAuth();
  const query = useQuery<Subscription | null>({
    queryKey: queryKeys.billing.subscription,
    queryFn: async () => edenData(await eden.v1.billing.subscription.get()),
    enabled: isAuthenticated,
  });
  const { refetch: refetchQuery } = query;
  const refetch = useCallback(async () => {
    await refetchQuery();
  }, [refetchQuery]);

  return {
    subscription: query.data ?? null,
    loading: isAuthenticated && query.isPending,
    error: query.isError ? i18n.t("subscription.loadFailed", { ns: "common" }) : null,
    refetch,
  };
}
